import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorMsg } from "./ErrorMsg";
import { Label } from "./Label";

interface OptionProps {
    id: string;
    name: string;
}

interface MultiSelectDropdownProps {
    initialOptions?: OptionProps[];
    options?: OptionProps[];
    onChange?: (selectedOptions: OptionProps[]) => void;
    placeholder?: string;
    delay?: number;
    disabled?: boolean;
    isError?: boolean;
    errorMessage?: string;
    label?: string;
    required?: boolean;
    position?: "bottom" | "top";
}

const MultiSelectDropdown = ({
    initialOptions = [],
    options = [],
    onChange,
    placeholder = "Select options...",
    delay = 300,
    disabled = false,
    isError = false,
    errorMessage,
    label,
    required = false,
    position = "bottom",
}: MultiSelectDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
        position,
    );
    const [selectedOptions, setSelectedOptions] = useState<OptionProps[]>(
        initialOptions.length > 0 ? initialOptions : [],
    );

    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setDropdownPosition(position);
    }, [position]);

    useEffect(() => {
        if (initialOptions.length === 0) {
            setSelectedOptions([]);
        } else {
            setSelectedOptions(initialOptions);
        }
    }, [initialOptions]);

    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, delay);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchQuery, delay]);

    const filteredOptions = useMemo(() => {
        if (!debouncedSearchQuery) return options;

        return options.filter((option) =>
            option.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()),
        );
    }, [options, debouncedSearchQuery]);

    const calculateDropdownPosition = useCallback(() => {
        if (!triggerRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const dropdownHeight = 300;
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - triggerRect.bottom;

        setDropdownPosition(
            spaceBelow < dropdownHeight && triggerRect.top > dropdownHeight
                ? "top"
                : "bottom",
        );
    }, []);

    const handleSelect = useCallback(
        (option: OptionProps) => {
            setSelectedOptions((prev) => {
                const isSelected = prev.some((item) => item.id === option.id);
                const newSelection = isSelected
                    ? prev.filter((item) => item.id !== option.id)
                    : [...prev, option];

                if (onChange) {
                    onChange(newSelection);
                }
                return newSelection;
            });
        },
        [onChange],
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);

            const searchInput = searchInputRef.current;
            if (searchInput) {
                const focusTimer = setTimeout(() => searchInput.focus(), 0);
                return () => {
                    document.removeEventListener("mousedown", handleClickOutside);
                    clearTimeout(focusTimer);
                };
            }
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open]);

    useEffect(() => {
        const handleResize = () => {
            if (open) {
                calculateDropdownPosition();
            }
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [open, calculateDropdownPosition]);

    const toggleDropdown = useCallback(() => {
        if (!disabled) {
            setOpen((prevOpen) => {
                if (!prevOpen) {
                    setSearchQuery("");
                    setDebouncedSearchQuery("");
                }
                return !prevOpen;
            });
        }
    }, [disabled]);

    return (
        <div className="w-full text-xs 2xl:text-sm">
            {label && (
                <Label text={label} required={required} className="mb-1.5 2xl:mb-2" />
            )}

            <div ref={dropdownRef} className="relative w-full">
                <button
                    id="multi-select"
                    ref={triggerRef}
                    type="button"
                    className={`relative flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors ${disabled
                        ? "cursor-not-allowed bg-gray-100 opacity-50"
                        : "cursor-pointer hover:bg-gray-50"
                        } ${isError
                            ? "border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                            : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                        }`}
                    onClick={toggleDropdown}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    disabled={disabled}
                    aria-labelledby="multi-select"
                >
                    <div className="flex max-w-[90%] flex-wrap gap-1 overflow-hidden">
                        {selectedOptions.length > 0 ? (
                            <span>{selectedOptions.length} selected</span>
                        ) : (
                            <span className="text-gray-300">{placeholder}</span>
                        )}
                    </div>

                    <svg
                        className="w-3.h-3.5 h-3.5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className={`thin-scrollbar 2xl:max-h-62 absolute z-10 max-h-56 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg ${dropdownPosition === "top"
                                ? "bottom-full mb-1"
                                : "top-full mt-1"
                                }`}
                            role="listbox"
                        >
                            <div className="sticky top-0 z-10 border-b border-gray-300 bg-white p-2">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                                    placeholder="Search options..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    aria-labelledby="multi-select"
                                />
                            </div>

                            <ul className="overflow-auto py-1">
                                {filteredOptions.length === 0 ? (
                                    <li className="px-3 py-2 text-gray-400">
                                        No matching options found
                                    </li>
                                ) : (
                                    filteredOptions.map((option) => {
                                        const isSelected = selectedOptions.some(
                                            (item) => item.id === option.id,
                                        );
                                        return (
                                            <li
                                                key={option.id}
                                                className={`flex cursor-pointer items-center px-3 py-2 ${isSelected
                                                    ? "bg-indigo-100 text-primary"
                                                    : "hover:bg-gray-100"
                                                    }`}
                                                onClick={() => handleSelect(option)}
                                                role="option"
                                                aria-selected={isSelected}
                                            >
                                                <div
                                                    className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected
                                                        ? "border-indigo-500 bg-indigo-500"
                                                        : "border-gray-300"
                                                        }`}
                                                >
                                                    {isSelected && (
                                                        <svg
                                                            className="h-3 w-3 text-white"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                                {option.name}
                                            </li>
                                        );
                                    })
                                )}
                            </ul>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {isError && errorMessage && <ErrorMsg text={errorMessage} />}
        </div>
    );
};

export { MultiSelectDropdown };

